import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  Users,
  Search,
  BarChart3,
  CreditCard,
  FileText,
  Loader2,
  RefreshCw,
  Shield,
  Crown,
  Trash2,
  Globe,
  KeyRound,
  UserPlus,
  TrendingUp,
  Mail,
  Coins,
  Gift,
  LogIn,
  Clock
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);
  
  // Grant credits state
  const [grantCreditsDialogOpen, setGrantCreditsDialogOpen] = useState(false);
  const [userToGrantCredits, setUserToGrantCredits] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [grantingCredits, setGrantingCredits] = useState(false);
  
  // Login as user state
  const [loginAsUserLoading, setLoginAsUserLoading] = useState(null);

  useEffect(() => {
    if (!authLoading && user) {
      fetchData();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);

  const fetchData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/api/admin/users'),
        api.get('/api/admin/stats')
      ]);
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingUser(userId);
    try {
      await api.put(`/api/admin/users/${userId}/role`, null, { params: { role: newRole } });
      toast.success(`Role updated to ${newRole}`);
      const usersRes = await api.get('/api/admin/users');
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/users/${userToDelete.user_id}`);
      toast.success(`Deleted ${userToDelete.email}`);
      setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      await api.post('/api/admin/users/bulk-delete', { user_ids: selectedUsers });
      toast.success(`Deleted ${selectedUsers.length} users`);
      setUsers(users.filter(u => !selectedUsers.includes(u.user_id)));
      setSelectedUsers([]);
      setBulkDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!userToReset) return;
    setSendingReset(true);
    try {
      await api.post(`/api/admin/users/${userToReset.user_id}/reset-password-email`);
      toast.success(`Password reset email sent to ${userToReset.email}`);
      setResetPasswordDialogOpen(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send reset email';
      toast.error(msg);
    } finally {
      setSendingReset(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleGrantCredits = async () => {
    if (!userToGrantCredits || !creditsAmount) return;
    
    const amount = parseInt(creditsAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }
    
    setGrantingCredits(true);
    try {
      await api.post('/api/credits/add', null, {
        params: {
          user_id: userToGrantCredits.user_id,
          credits: amount,
          reason: creditsReason || 'Admin granted credits'
        }
      });
      toast.success(`Granted ${amount.toLocaleString()} credits to ${userToGrantCredits.email}`);
      setGrantCreditsDialogOpen(false);
      setUserToGrantCredits(null);
      setCreditsAmount('');
      setCreditsReason('');
      // Refresh users list
      const usersRes = await api.get('/api/admin/users');
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to grant credits: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGrantingCredits(false);
    }
  };

  const handleLoginAsUser = async (targetUser) => {
    if (!targetUser) return;
    
    setLoginAsUserLoading(targetUser.user_id);
    try {
      const response = await api.post(`/api/admin/login-as-user/${targetUser.user_id}`);
      
      // Store the new session token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      // Use the login function from context to update the user state
      await login(response.data.user.email, null, true, response.data);
      
      toast.success(`Now logged in as ${targetUser.email}`);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to login as user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoginAsUserLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getRoleDisplay = (u) => u.role === 'admin' ? 'admin' : (u.plan || 'free');
  
  const roleColors = { 
    free: 'bg-slate-100 text-slate-700', 
    standard: 'bg-blue-100 text-blue-700', 
    pro: 'bg-purple-100 text-purple-700', 
    scale: 'bg-indigo-100 text-indigo-700',
    business: 'bg-amber-100 text-amber-700',
    enterprise: 'bg-amber-100 text-amber-700', 
    admin: 'bg-red-100 text-red-700' 
  };

  const authTypeIcons = {
    google: '🔵',
    microsoft: '🟦',
    email: '✉️'
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.utm?.source?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const sortedUsers = [...filteredUsers].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-slate-600 mb-6">You don&apos;t have permission to access the admin panel.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold">CRM Data Cleaner</span>
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">Admin</span>
          </Link>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Title & Refresh */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Manage users, view stats, and perform admin actions</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{(stats.total_users || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Total Users</p>
            </div>

            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <UserPlus className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{(stats.signups_today || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Signups Today</p>
            </div>

            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{(stats.paid_users || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Paid Users</p>
            </div>

            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{(stats.total_rows_this_month || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Rows This Month</p>
            </div>

            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{stats.avg_time_spent_per_1k_rows_seconds || 3}s</p>
              <p className="text-sm text-slate-500">Avg. time spent / 1K rows</p>
            </div>

            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-rose-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-rose-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">
                {stats.paid_users && stats.total_users 
                  ? ((stats.paid_users / stats.total_users) * 100).toFixed(1) 
                  : 0}%
              </p>
              <p className="text-sm text-slate-500">Conversion Rate</p>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b flex justify-between items-center flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold">Users ({filteredUsers.length})</h2>
              {selectedUsers.length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedUsers.length})
                </Button>
              )}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Search by email, name, or UTM..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="pl-9 w-72" 
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox 
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0} 
                    onCheckedChange={() => setSelectedUsers(
                      selectedUsers.length === filteredUsers.length ? [] : filteredUsers.map(u => u.user_id)
                    )} 
                  />
                </TableHead>
                <TableHead>User</TableHead>
                <TableHead>Auth</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map(u => (
                <React.Fragment key={u.user_id}>
                  <TableRow className="hover:bg-slate-50">
                    <TableCell>
                      <Checkbox 
                        checked={selectedUsers.includes(u.user_id)} 
                        onCheckedChange={() => toggleUserSelection(u.user_id)} 
                      />
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer" 
                      onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                    >
                      <div className="flex items-center gap-2">
                        {u.role === 'admin' && <Crown className="w-4 h-4 text-amber-500" />}
                        <div>
                          <p className="font-medium">{u.name || 'No name'}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span title={u.auth_type || 'email'}>
                        {authTypeIcons[u.auth_type] || '✉️'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${roleColors[getRoleDisplay(u)]}`}>
                        {getRoleDisplay(u)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.utm?.source ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Globe className="w-3 h-3" />
                          {u.utm.source}
                          {u.utm.medium && (
                            <span className="text-slate-400">/ {u.utm.medium}</span>
                          )}
                        </div>
                      ) : u.referred_by ? (
                        <span className="text-emerald-600 text-sm">ref: {u.referred_by}</span>
                      ) : (
                        <span className="text-slate-400 text-sm">Direct</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">{formatDate(u.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Select 
                          value={getRoleDisplay(u)} 
                          onValueChange={(v) => handleRoleChange(u.user_id, v)} 
                          disabled={updatingUser === u.user_id}
                        >
                          <SelectTrigger className="w-28 h-8">
                            {updatingUser === u.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <SelectValue />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="scale">Scale</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="standard">Standard (Legacy)</SelectItem>
                            <SelectItem value="enterprise">Enterprise (Legacy)</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Login as User */}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8 text-emerald-500 hover:bg-emerald-50" 
                          onClick={() => handleLoginAsUser(u)}
                          disabled={loginAsUserLoading === u.user_id || u.role === 'admin'}
                          title={u.role === 'admin' ? "Cannot impersonate admin users" : "Login as this user"}
                          data-testid={`login-as-user-${u.user_id}`}
                        >
                          {loginAsUserLoading === u.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <LogIn className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {/* Password Reset - show for non-Google users */}
                        {u.auth_type !== 'google' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-indigo-500 hover:bg-indigo-50" 
                            onClick={() => { setUserToReset(u); setResetPasswordDialogOpen(true); }}
                            title="Send password reset email"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Grant Credits */}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8 text-amber-500 hover:bg-amber-50" 
                          onClick={() => { setUserToGrantCredits(u); setGrantCreditsDialogOpen(true); }}
                          title="Grant credits"
                        >
                          <Coins className="w-4 h-4" />
                        </Button>
                        
                        {/* Delete */}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:bg-red-50" 
                          onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row */}
                  {expandedUser === u.user_id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-slate-50">
                        <div className="p-4 grid md:grid-cols-5 gap-4">
                          <div>
                            <p className="text-sm text-slate-500">User ID</p>
                            <p className="font-mono text-xs">{u.user_id}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Files This Month</p>
                            <p className="font-semibold">{u.usage?.files_this_month || 0}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Rows Cleaned</p>
                            <p className="font-semibold">{(u.usage?.rows_this_month || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Credits Balance</p>
                            <p className="font-semibold text-amber-600">{(u.credits_balance || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Referral Code</p>
                            <p className="font-mono text-xs">{u.referral_code || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-5 gap-4 mt-3 pt-3 border-t border-slate-200">
                          <div>
                            <p className="text-sm text-slate-500">Experience Level</p>
                            <p className={u.experience_level ? 'font-medium' : 'text-slate-400'}>
                              {u.experience_level ? (u.experience_level === 'consultant' ? '🧠 Consultant' : '🌱 Beginner') : 'Not set'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Onboarding</p>
                            <p className={u.onboarding_completed ? 'text-emerald-600' : 'text-amber-600'}>
                              {u.onboarding_completed ? '✓ Completed' : 'Pending'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">UTM Campaign</p>
                            <p>{u.utm?.campaign || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Referred By</p>
                            <p>{u.referred_by || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Referral Signups</p>
                            <p>{u.referral_stats?.signups || 0}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-5 gap-4 mt-3 pt-3 border-t border-slate-200">
                          <div>
                            <p className="text-sm text-slate-500">Referral Purchases</p>
                            <p>{u.referral_stats?.purchases || 0}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Referral Credits Earned</p>
                            <p className="text-emerald-600">{(u.referral_stats?.total_credits_earned || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              
              {sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Delete Single User Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.email}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser} 
              disabled={deleting} 
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedUsers.length} Users</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All selected users and their data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              disabled={deleting} 
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <AlertDialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Send Password Reset Email
            </AlertDialogTitle>
            <AlertDialogDescription>
              Send a password reset email to <strong>{userToReset?.email}</strong>?
              <br /><br />
              They will receive a link to create a new password. The link expires in 60 minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSendPasswordReset} 
              disabled={sendingReset} 
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {sendingReset && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grant Credits Dialog */}
      <AlertDialog open={grantCreditsDialogOpen} onOpenChange={setGrantCreditsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              Grant Credits
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-4">
                  Grant credits to <strong>{userToGrantCredits?.email}</strong>
                </p>
                <p className="text-sm text-slate-500 mb-2">
                  Current balance: <strong>{(userToGrantCredits?.credits_balance || 0).toLocaleString()}</strong> credits
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Credits Amount</label>
                    <Input
                      type="number"
                      placeholder="Enter amount (e.g., 1000)"
                      value={creditsAmount}
                      onChange={(e) => setCreditsAmount(e.target.value)}
                      className="mt-1"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Reason (optional)</label>
                    <Input
                      type="text"
                      placeholder="e.g., Customer support, Promo code..."
                      value={creditsReason}
                      onChange={(e) => setCreditsReason(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setCreditsAmount(''); setCreditsReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleGrantCredits} 
              disabled={grantingCredits || !creditsAmount} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              {grantingCredits && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Grant Credits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
